import React, { useState, useEffect } from 'react';
import { FaPlus, FaEdit, FaTrash, FaEye, FaStar, FaImage, FaGripVertical, FaExternalLinkAlt, FaGithub } from 'react-icons/fa';
import PageHeader from './PageHeader';
import './PortfolioManager.scss';

interface Portfolio {
  id: number;
  title: string;
  slug: string;
  category: string;
  image: string;
  description: string;
  longDescription?: string;
  technologies: string[];
  liveUrl?: string;
  githubUrl?: string;
  clientName?: string;
  projectDuration?: string;
  featured: boolean;
  order: number;
  status: 'published' | 'draft';
  views: number;
  createdAt: string;
  updatedAt: string;
}

const PortfolioManager: React.FC = () => {
  const [portfolios, setPortfolios] = useState<Portfolio[]>([]);
  const [filteredPortfolios, setFilteredPortfolios] = useState<Portfolio[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState<Portfolio | null>(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    category: 'web',
    description: '',
    longDescription: '',
    technologies: '',
    liveUrl: '',
    githubUrl: '',
    clientName: '',
    projectDuration: '',
    featured: false,
    order: 0,
    status: 'published' as 'published' | 'draft'
  });
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>('');

  const categories = [
    { value: 'web', label: 'Web Development' },
    { value: 'mobile', label: 'Mobile Apps' },
    { value: 'design', label: 'UI/UX Design' },
    { value: 'ecommerce', label: 'E-commerce' },
    { value: 'saas', label: 'SaaS' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchPortfolios();
  }, []);

  useEffect(() => {
    filterPortfoliosData();
  }, [portfolios, filterCategory, filterStatus]);

  const fetchPortfolios = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('http://localhost:4000/api/portfolio/admin/all', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setPortfolios(data);
      }
    } catch (error) {
      console.error('Error fetching portfolios:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterPortfoliosData = () => {
    let filtered = [...portfolios];

    if (filterCategory !== 'all') {
      filtered = filtered.filter(p => p.category === filterCategory);
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(p => p.status === filterStatus);
    }

    setFilteredPortfolios(filtered);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const token = localStorage.getItem('adminToken');
    const formDataToSend = new FormData();

    formDataToSend.append('title', formData.title);
    formDataToSend.append('category', formData.category);
    formDataToSend.append('description', formData.description);
    formDataToSend.append('longDescription', formData.longDescription);
    
    // Parse technologies from comma-separated string to array
    const techArray = formData.technologies.split(',').map(t => t.trim()).filter(t => t);
    formDataToSend.append('technologies', JSON.stringify(techArray));
    
    formDataToSend.append('liveUrl', formData.liveUrl);
    formDataToSend.append('githubUrl', formData.githubUrl);
    formDataToSend.append('clientName', formData.clientName);
    formDataToSend.append('projectDuration', formData.projectDuration);
    formDataToSend.append('featured', formData.featured.toString());
    formDataToSend.append('order', formData.order.toString());
    formDataToSend.append('status', formData.status);

    if (imageFile) {
      formDataToSend.append('image', imageFile);
    }

    try {
      const url = editingPortfolio
        ? `http://localhost:4000/api/portfolio/admin/${editingPortfolio.id}`
        : 'http://localhost:4000/api/portfolio/admin';

      const method = editingPortfolio ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (response.ok) {
        alert(editingPortfolio ? 'Portfolio updated successfully!' : 'Portfolio created successfully!');
        setShowModal(false);
        resetForm();
        fetchPortfolios();
      } else {
        const error = await response.json();
        alert(`Error: ${error.message}`);
      }
    } catch (error) {
      console.error('Error saving portfolio:', error);
      alert('Error saving portfolio');
    }
  };

  const handleEdit = (portfolio: Portfolio) => {
    setEditingPortfolio(portfolio);
    setFormData({
      title: portfolio.title,
      category: portfolio.category,
      description: portfolio.description,
      longDescription: portfolio.longDescription || '',
      technologies: portfolio.technologies.join(', '),
      liveUrl: portfolio.liveUrl || '',
      githubUrl: portfolio.githubUrl || '',
      clientName: portfolio.clientName || '',
      projectDuration: portfolio.projectDuration || '',
      featured: portfolio.featured,
      order: portfolio.order,
      status: portfolio.status
    });
    setImagePreview(`http://localhost:4000${portfolio.image}`);
    setShowModal(true);
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Are you sure you want to delete this portfolio?')) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`http://localhost:4000/api/portfolio/admin/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('Portfolio deleted successfully!');
        fetchPortfolios();
      } else {
        alert('Error deleting portfolio');
      }
    } catch (error) {
      console.error('Error deleting portfolio:', error);
      alert('Error deleting portfolio');
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      category: 'web',
      description: '',
      longDescription: '',
      technologies: '',
      liveUrl: '',
      githubUrl: '',
      clientName: '',
      projectDuration: '',
      featured: false,
      order: 0,
      status: 'published'
    });
    setImageFile(null);
    setImagePreview('');
    setEditingPortfolio(null);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  // Statistics
  const stats = {
    total: portfolios.length,
    published: portfolios.filter(p => p.status === 'published').length,
    drafts: portfolios.filter(p => p.status === 'draft').length,
    featured: portfolios.filter(p => p.featured).length,
    totalViews: portfolios.reduce((sum, p) => sum + p.views, 0)
  };

  if (loading) {
    return <div className="portfolio-manager__loading">Loading portfolios...</div>;
  }

  return (
    <div className="portfolio-manager">
      <PageHeader 
        title="Portfolio Manager" 
        subtitle="Manage your portfolio projects and showcase your work"
      />
      <div className="portfolio-manager__header">
        <button className="portfolio-manager__add-btn" onClick={() => setShowModal(true)}>
          <FaPlus /> Add New Portfolio
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="portfolio-manager__stats">
        <div className="portfolio-manager__stat-card">
          <div className="portfolio-manager__stat-icon portfolio-manager__stat-icon--total">
            <FaImage />
          </div>
          <div className="portfolio-manager__stat-content">
            <h3>{stats.total}</h3>
            <p>Total Projects</p>
          </div>
        </div>
        <div className="portfolio-manager__stat-card">
          <div className="portfolio-manager__stat-icon portfolio-manager__stat-icon--published">
            <FaEye />
          </div>
          <div className="portfolio-manager__stat-content">
            <h3>{stats.published}</h3>
            <p>Published</p>
          </div>
        </div>
        <div className="portfolio-manager__stat-card">
          <div className="portfolio-manager__stat-icon portfolio-manager__stat-icon--draft">
            <FaEdit />
          </div>
          <div className="portfolio-manager__stat-content">
            <h3>{stats.drafts}</h3>
            <p>Drafts</p>
          </div>
        </div>
        <div className="portfolio-manager__stat-card">
          <div className="portfolio-manager__stat-icon portfolio-manager__stat-icon--featured">
            <FaStar />
          </div>
          <div className="portfolio-manager__stat-content">
            <h3>{stats.featured}</h3>
            <p>Featured</p>
          </div>
        </div>
        <div className="portfolio-manager__stat-card">
          <div className="portfolio-manager__stat-icon portfolio-manager__stat-icon--views">
            <FaEye />
          </div>
          <div className="portfolio-manager__stat-content">
            <h3>{stats.totalViews.toLocaleString()}</h3>
            <p>Total Views</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="portfolio-manager__filters">
        <div className="portfolio-manager__filter-group">
          <label>Category:</label>
          <select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat.value} value={cat.value}>{cat.label}</option>
            ))}
          </select>
        </div>
        <div className="portfolio-manager__filter-group">
          <label>Status:</label>
          <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Status</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </select>
        </div>
      </div>

      {/* Portfolio Table */}
      <div className="portfolio-manager__table-container">
        <table className="portfolio-manager__table">
          <thead>
            <tr>
              <th>Order</th>
              <th>Image</th>
              <th>Title</th>
              <th>Category</th>
              <th>Technologies</th>
              <th>Status</th>
              <th>Featured</th>
              <th>Views</th>
              <th>Links</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredPortfolios.length === 0 ? (
              <tr>
                <td colSpan={10} style={{ textAlign: 'center', padding: '40px' }}>
                  No portfolios found
                </td>
              </tr>
            ) : (
              filteredPortfolios.map(portfolio => (
                <tr key={portfolio.id}>
                  <td>
                    <div className="portfolio-manager__order">
                      <FaGripVertical />
                      <span>{portfolio.order}</span>
                    </div>
                  </td>
                  <td>
                    <img 
                      src={`http://localhost:4000${portfolio.image}`} 
                      alt={portfolio.title}
                      className="portfolio-manager__thumbnail"
                    />
                  </td>
                  <td>
                    <div className="portfolio-manager__title-cell">
                      <strong>{portfolio.title}</strong>
                      <small>{portfolio.slug}</small>
                    </div>
                  </td>
                  <td>
                    <span className={`portfolio-manager__badge portfolio-manager__badge--${portfolio.category}`}>
                      {categories.find(c => c.value === portfolio.category)?.label}
                    </span>
                  </td>
                  <td>
                    <div className="portfolio-manager__tech-tags">
                      {portfolio.technologies.slice(0, 3).map((tech, idx) => (
                        <span key={idx} className="portfolio-manager__tech-tag">{tech}</span>
                      ))}
                      {portfolio.technologies.length > 3 && (
                        <span className="portfolio-manager__tech-tag">+{portfolio.technologies.length - 3}</span>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className={`portfolio-manager__status portfolio-manager__status--${portfolio.status}`}>
                      {portfolio.status}
                    </span>
                  </td>
                  <td className="portfolio-manager__centered">
                    {portfolio.featured && <FaStar className="portfolio-manager__featured-icon" />}
                  </td>
                  <td className="portfolio-manager__centered">
                    <div className="portfolio-manager__views">
                      <FaEye /> {portfolio.views}
                    </div>
                  </td>
                  <td>
                    <div className="portfolio-manager__links">
                      {portfolio.liveUrl && (
                        <a href={portfolio.liveUrl} target="_blank" rel="noopener noreferrer" title="Live URL">
                          <FaExternalLinkAlt />
                        </a>
                      )}
                      {portfolio.githubUrl && (
                        <a href={portfolio.githubUrl} target="_blank" rel="noopener noreferrer" title="GitHub">
                          <FaGithub />
                        </a>
                      )}
                    </div>
                  </td>
                  <td>
                    <div className="portfolio-manager__actions">
                      <button 
                        className="portfolio-manager__btn portfolio-manager__btn--edit"
                        onClick={() => handleEdit(portfolio)}
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button 
                        className="portfolio-manager__btn portfolio-manager__btn--delete"
                        onClick={() => handleDelete(portfolio.id)}
                        title="Delete"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="portfolio-manager__modal-overlay" onClick={handleCloseModal}>
          <div className="portfolio-manager__modal" onClick={(e) => e.stopPropagation()}>
            <div className="portfolio-manager__modal-header">
              <h2>{editingPortfolio ? 'Edit Portfolio' : 'Add New Portfolio'}</h2>
              <button className="portfolio-manager__close-btn" onClick={handleCloseModal}>×</button>
            </div>
            <form onSubmit={handleSubmit} className="portfolio-manager__form">
              <div className="portfolio-manager__form-row">
                <div className="portfolio-manager__form-group">
                  <label>Title *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    placeholder="Project Title"
                  />
                </div>
                <div className="portfolio-manager__form-group">
                  <label>Category *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="portfolio-manager__form-group">
                <label>Short Description *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  required
                  rows={3}
                  placeholder="Brief description (shown on portfolio cards)"
                  maxLength={500}
                />
                <small>{formData.description.length}/500 characters</small>
              </div>

              <div className="portfolio-manager__form-group">
                <label>Long Description</label>
                <textarea
                  name="longDescription"
                  value={formData.longDescription}
                  onChange={handleInputChange}
                  rows={5}
                  placeholder="Detailed project description (optional)"
                />
              </div>

              <div className="portfolio-manager__form-group">
                <label>Technologies * (comma-separated)</label>
                <input
                  type="text"
                  name="technologies"
                  value={formData.technologies}
                  onChange={handleInputChange}
                  required
                  placeholder="React, Node.js, MongoDB, AWS"
                />
                <small>Enter technologies separated by commas</small>
              </div>

              <div className="portfolio-manager__form-row">
                <div className="portfolio-manager__form-group">
                  <label>Live URL</label>
                  <input
                    type="url"
                    name="liveUrl"
                    value={formData.liveUrl}
                    onChange={handleInputChange}
                    placeholder="https://example.com"
                  />
                </div>
                <div className="portfolio-manager__form-group">
                  <label>GitHub URL</label>
                  <input
                    type="url"
                    name="githubUrl"
                    value={formData.githubUrl}
                    onChange={handleInputChange}
                    placeholder="https://github.com/username/repo"
                  />
                </div>
              </div>

              <div className="portfolio-manager__form-row">
                <div className="portfolio-manager__form-group">
                  <label>Client Name</label>
                  <input
                    type="text"
                    name="clientName"
                    value={formData.clientName}
                    onChange={handleInputChange}
                    placeholder="Client or Company Name"
                  />
                </div>
                <div className="portfolio-manager__form-group">
                  <label>Project Duration</label>
                  <input
                    type="text"
                    name="projectDuration"
                    value={formData.projectDuration}
                    onChange={handleInputChange}
                    placeholder="e.g., 3 months, 6 weeks"
                  />
                </div>
              </div>

              <div className="portfolio-manager__form-row">
                <div className="portfolio-manager__form-group">
                  <label>Order</label>
                  <input
                    type="number"
                    name="order"
                    value={formData.order}
                    onChange={handleInputChange}
                    min="0"
                    placeholder="0"
                  />
                  <small>Lower numbers appear first</small>
                </div>
                <div className="portfolio-manager__form-group">
                  <label>Status</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="published">Published</option>
                    <option value="draft">Draft</option>
                  </select>
                </div>
              </div>

              <div className="portfolio-manager__form-group">
                <label className="portfolio-manager__checkbox-label">
                  <input
                    type="checkbox"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleInputChange}
                  />
                  <span>Featured Project</span>
                </label>
              </div>

              <div className="portfolio-manager__form-group">
                <label>Project Image * {editingPortfolio && '(leave empty to keep current image)'}</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  required={!editingPortfolio}
                />
                {imagePreview && (
                  <div className="portfolio-manager__image-preview">
                    <img src={imagePreview} alt="Preview" />
                  </div>
                )}
              </div>

              <div className="portfolio-manager__form-actions">
                <button type="button" className="portfolio-manager__cancel-btn" onClick={handleCloseModal}>
                  Cancel
                </button>
                <button type="submit" className="portfolio-manager__submit-btn">
                  {editingPortfolio ? 'Update Portfolio' : 'Create Portfolio'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default PortfolioManager;

